import { Platform, View } from 'react-native';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Text,
  Input,
  Banner,
  Footer,
  Select,
  KeyboardScreenContainer,
  DontKnowCep,
  Checkbox,
  InputFeedback,
} from '~/components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useMarisaAutoForm from '~/stores/marisaAutoForm';
import type { MarisaAutoStepsProps } from '../..';
import { states } from '~/utils/string';
import { getAddressByCepUseCase } from '~/useCases/AssegurouAutoUseCases/GetAddressByCepUseCase';
import { isWeb } from '~/utils/device';

const schema = yup
  .object()
  .shape({
    zipcode: yup.string().required('Este campo é obrigatório'),
    street: yup.string().required('Este campo é obrigatório'),
    neighborhood: yup.string().required('Este campo é obrigatório'),
    city: yup.string().required('Este campo é obrigatório'),
    state: yup.string().required('.'),
    noNumber: yup.boolean(),
    noComplement: yup.boolean(),
    number: yup.string().when('noNumber', {
      is: false,
      then: yup
        .string()
        .required(
          `Informe o número da residência ou toque no checkbox "Sem número".`
        ),
    }),
    complement: yup.string().when('noComplement', {
      is: false,
      then: yup
        .string()
        .required(
          `Informe o complemento ou toque no checkbox "Sem complemento".`
        ),
    }),
  })
  .required();

interface ZipCodeForm {
  zipcode: string;
  street: string;
  neighborhood: string;
  city: string;
  state: string;
  noNumber: boolean;
  number: string;
  complement: string;
  noComplement: boolean;
}

const ZipCode = ({ onContinue }: MarisaAutoStepsProps) => {
  const [loadingZipCode, setLoadingZipCode] = useState<boolean>(false);
  const [loadedZipCode, setLoadedZipCode] = useState<boolean>(false);

  const {
    control,
    watch,
    setValue,
    setError,
    clearErrors,
    handleSubmit,
    formState: { errors },
  } = useForm<ZipCodeForm>({
    resolver: yupResolver(schema),
    defaultValues: { noNumber: false, noComplement: false },
  });

  const { setAddress } = useMarisaAutoForm();

  const zipcode = watch('zipcode');
  const hasNoNumber = watch('noNumber');
  const hasNoComplement = watch('noComplement');

  const onSubmit = useCallback(
    (data: ZipCodeForm) => {
      setAddress(data);

      onContinue();
    },
    [onContinue, setAddress]
  );

  useEffect(() => {
    async function fetchZipCode() {
      setLoadingZipCode(true);
      clearErrors();

      const addressResponse = await getAddressByCepUseCase(zipcode);

      if (addressResponse.success) {
        const { street, neighborhood, city, state } = addressResponse.address;

        setLoadedZipCode(true);
        setValue('street', street.toUpperCase());
        setValue('neighborhood', neighborhood.toUpperCase());
        setValue('city', city.toUpperCase());
        setValue('state', state.toUpperCase());
        setLoadingZipCode(false);
        setError('zipcode', {});
      } else {
        setError('zipcode', { message: 'CEP não encontrado' });
        setLoadedZipCode(false);
        setLoadingZipCode(false);
      }
    }

    if (zipcode?.length === 9) {
      fetchZipCode();
    }
  }, [zipcode, setError, setValue, clearErrors]);

  return (
    <KeyboardScreenContainer
      footer={
        <View
          className={`flex xl:hidden ${
            Platform.OS === 'web' ? 'py-4 px-6' : ''
          }`}
        >
          <Footer onSubmit={handleSubmit(onSubmit)} />
        </View>
      }
    >
      <View className="flex-1 container mx-auto xl:flex-row xl:pt-12 xl:pb-24">
        <Banner type="marisa-auto" />

        <View className="mt-8 xl:mt-0 px-6 flex-1 xl:ml-24">
          <Text className="text-xl mb-4 text-gray-600">
            Qual o CEP onde o carro passa a noite?
          </Text>

          <Text className="text-gray-400 text-sm mb-8">
            A região influencia no risco de roubos e furtos e sabermos onde o
            carro passa a noite pode trazer melhores preços
          </Text>

          <View>
            <Input
              loading={loadingZipCode}
              name="zipcode"
              label="Qual o seu CEP?"
              placeholder="Insira seu CEP sem hífen"
              control={control}
              className="mb-[-20px]"
              maskConfig={{
                type: 'zip-code',
              }}
              maxLength={9}
              error={errors.zipcode?.message}
              keyboardType="number-pad"
            />
            <DontKnowCep errorMessage={errors.zipcode?.message} />
          </View>

          <View className={`${loadedZipCode ? 'opacity-1' : 'hidden'} `}>
            <Input
              control={control}
              label="Logradouro"
              name="street"
              keyboardType="ascii-capable"
              error={errors.street?.message}
              maxLength={150}
            />

            <View className="flex-row items-center">
              <View className="flex-[0.5] mb-6">
                <Input
                  control={control}
                  label="Número"
                  name="number"
                  editable={!hasNoNumber}
                  maskConfig={{ type: 'only-numbers' }}
                  keyboardType="number-pad"
                  placeholder="Ex.: 123"
                  className={`${isWeb ? '' : 'flex-[0.5]'}`}
                  maxLength={10}
                />

                <Checkbox
                  control={control}
                  name="noNumber"
                  className={'mt-[-20px]'}
                  error={errors.number?.message}
                >
                  Sem número
                </Checkbox>
              </View>

              <View className="flex-[0.5] mb-6 ml-2">
                <Input
                  control={control}
                  label="Complemento"
                  name="complement"
                  editable={!hasNoComplement}
                  keyboardType="ascii-capable"
                  placeholder="Ex.: Casa 2"
                  maxLength={10}
                />
                <Checkbox
                  control={control}
                  name="noComplement"
                  className={'mt-[-20px]'}
                  error={errors.complement?.message}
                >
                  Sem complemento
                </Checkbox>
              </View>
            </View>

            <InputFeedback
              message={errors.number?.message}
              className="mt-[-12px]"
            />

            <InputFeedback
              message={errors.complement?.message}
              className={`${errors.number?.message ? '' : 'mt-[-12px]'}`}
            />

            <Input
              control={control}
              label="Bairro"
              name="neighborhood"
              keyboardType="ascii-capable"
              error={errors.neighborhood?.message}
              maxLength={150}
            />

            <View className="flex-row items-center mb-4 z-10">
              <Input
                control={control}
                label="Cidade"
                name="city"
                error={errors.city?.message}
                keyboardType="ascii-capable"
                className="mr-2 flex-[0.7]"
                maxLength={150}
              />
              <Select
                control={control}
                label="Estado"
                name="state"
                placeholder="Selecione"
                error={errors.state?.message}
                items={states}
                className="flex-[0.3]"
              />
            </View>
          </View>

          <Footer className="hidden" onSubmit={handleSubmit(onSubmit)} />
        </View>
      </View>
    </KeyboardScreenContainer>
  );
};

export default ZipCode;
